import { useState, useEffect, Dispatch, SetStateAction } from 'react';
import { Input } from '@acb_devel/design-system';
import { Heading } from '@acb_devel/design-system';
import { Text } from '@acb_devel/design-system';
import { Skeleton } from '@acb_devel/design-system';
import { EditIcon } from '@acb_devel/design-system';
import { Button } from '@acb_devel/design-system';
import { CheckIcon, XMarkIcon } from '@acb_devel/design-system';
import { useUserContext } from '@contexts/UserContext';
import { ROLE_ADMIN } from '@services/users';

import styles from './ProfileCardItem.module.scss';

function EditButton({
  fieldName,
  setEdit,
}: {
  fieldName: string;
  setEdit: Dispatch<SetStateAction<boolean>>;
}) {
  const { user } = useUserContext();

  if (user === undefined) {
    return <></>;
  }

  if (user.role === ROLE_ADMIN) {
    return (
      <Button
        variant="flat"
        size="md"
        color="secondary"
        isOnlyIcon
        onClick={() => setEdit(true)}
        aria-label={`Editar ${fieldName.toLowerCase()}`}
      >
        <EditIcon />
      </Button>
    );
  }

  return <></>;
}

export function ProfileCardItem({
  title,
  value,
  loading,
  setValue,
}: {
  title: string;
  value: string | null | undefined;
  loading: boolean;
  setValue?: (value: string) => Promise<void>;
}) {
  const [edit, setEdit] = useState(false);
  const [tempValue, setTempValue] = useState(value ?? '');

  useEffect(() => {
    if (value) {
      setTempValue(value);
    }
  }, [value]);

  const handleSave = () => {
    if (setValue) {
      setValue(tempValue);
    }
    setEdit(false);
  };

  const handleCancel = () => {
    setTempValue(value ?? '');
    setEdit(false);
  };

  return (
    <div className={styles.profileCardItem}>
      <Heading
        as="p"
        variant="subhead"
        className={styles.profileCardItem__title}
      >
        {title}
      </Heading>
      {loading ? (
        <Text variant="body-2">
          <Skeleton>Loading...</Skeleton>
        </Text>
      ) : (
        <>
          {edit && setValue ? (
            <div className={styles.profileCardItem__edit}>
              <Input
                id={title}
                value={tempValue}
                onChange={(event) => setTempValue(event.target.value)}
                aria-label={title}
              />
              <div className={styles.profileCardItem__buttons}>
                <Button
                  variant="outline"
                  size="md"
                  color="primary"
                  onClick={handleSave}
                  aria-label={`Guardar ${title.toLowerCase()}`}
                >
                  <CheckIcon />
                </Button>
                <Button
                  variant="outline"
                  size="md"
                  color="secondary"
                  onClick={handleCancel}
                  aria-label={`Cancelar ${title.toLowerCase()}`}
                >
                  <XMarkIcon />
                </Button>
              </div>
            </div>
          ) : value ? (
            <Text variant="body-2" className={styles.profileCardItem__value}>
              <>
                {value?.split('<br>').map((item, index) => (
                  <span key={index}>
                    {item} <br />
                  </span>
                ))}
                {setValue && <EditButton setEdit={setEdit} fieldName={title} />}
              </>
            </Text>
          ) : (
            <>
              <Text variant="body-2" className={styles.profileCardItem__value}>
                <>
                  NS/NC{' '}
                  {setValue && (
                    <EditButton setEdit={setEdit} fieldName={title} />
                  )}
                </>
              </Text>
            </>
          )}
        </>
      )}
    </div>
  );
}
